import db from './mobileDb'

// Config
export async function getConfig(key) {
  const row = await db.config.get(key)
  return row?.value
}

export async function setConfig(key, value) {
  await db.config.put({ key, value })
}

// Profile cache
export async function cacheProfile(profile) {
  await db.profile.put(profile)
}

export async function getCachedProfile() {
  return db.profile.toCollection().first()
}

// Qualifications cache
export async function cacheQualifications(quals) {
  await db.transaction('rw', db.qualifications, async () => {
    await db.qualifications.clear()
    if (quals?.length) await db.qualifications.bulkPut(quals)
  })
}

export async function getCachedQualifications() {
  return db.qualifications.toArray()
}

// Leave balance cache
export async function cacheLeaveBalance(balances) {
  await db.transaction('rw', db.leaveBalance, async () => {
    await db.leaveBalance.clear()
    if (balances?.length) await db.leaveBalance.bulkPut(balances)
  })
}

export async function getCachedLeaveBalance() {
  return db.leaveBalance.toArray()
}

// Leave requests (bidirectional sync)
export async function cacheLeaveRequests(requests) {
  await db.transaction('rw', db.leaveRequests, async () => {
    const pending = await db.leaveRequests.where('syncStatus').equals('pending').toArray()
    await db.leaveRequests.clear()
    if (requests?.length) {
      await db.leaveRequests.bulkPut(requests.map(r => ({ ...r, syncStatus: 'synced' })))
    }
    if (pending?.length) {
      await db.leaveRequests.bulkAdd(pending)
    }
  })
}

export async function createOfflineLeaveRequest(request) {
  return db.leaveRequests.add({
    ...request,
    syncStatus: 'pending',
    createdAt: new Date().toISOString(),
  })
}

export async function getPendingLeaveRequests() {
  return db.leaveRequests.where('syncStatus').equals('pending').toArray()
}

// Checkins (bidirectional sync)
export async function cacheCheckins(checkins) {
  await db.transaction('rw', db.checkins, async () => {
    const pending = await db.checkins.where('syncStatus').equals('pending').toArray()
    await db.checkins.clear()
    if (checkins?.length) {
      await db.checkins.bulkPut(checkins.map(c => ({ ...c, syncStatus: 'synced' })))
    }
    if (pending?.length) {
      await db.checkins.bulkAdd(pending)
    }
  })
}

export async function createOfflineCheckin(event) {
  return db.checkins.add({
    ...event,
    syncStatus: 'pending',
    createdAt: new Date().toISOString(),
  })
}

export async function getPendingCheckins() {
  return db.checkins.where('syncStatus').equals('pending').toArray()
}

// Bookings cache
export async function cacheBookings(bookings) {
  await db.transaction('rw', db.bookings, async () => {
    await db.bookings.clear()
    if (bookings?.length) await db.bookings.bulkPut(bookings)
  })
}

export async function getCachedBookings() {
  return db.bookings.toArray()
}

// Sync helpers
export async function markSynced(table, localId, serverId) {
  await db[table].update(localId, { syncStatus: 'synced', id: serverId })
}

export async function markFailed(table, localId, error) {
  await db[table].update(localId, { syncStatus: 'failed', syncError: error })
}

export async function addSyncLog(entry) {
  await db.syncLog.add({ ...entry, timestamp: new Date().toISOString() })
}

export async function getLastSyncTime() {
  return getConfig('lastSyncTime')
}

export async function setLastSyncTime(time) {
  await setConfig('lastSyncTime', time || new Date().toISOString())
}

// The cache isn't keyed by person, so it is wiped on sign-out and when access is removed:
// otherwise the next person on a shared phone, or a removed member offline, sees these records.
export async function clearLocalData() {
  try {
    await Promise.all(db.tables.map(table => table.clear()))
  } catch (err) {
    console.error('[DB] Could not clear local data:', err)
  }
}
